import {Component, OnInit, ChangeDetectorRef} from '@angular/core';
import standardPaths from '../../standard_paths.json';
import { ActivatedRoute, Router } from '@angular/router';
import {HomePageHelpers} from '../../../pages/home/home.helpers';
import { CheckerDataService, CheckerSettings } from '../../services/checker-data.service';
import { HttpClient } from '@angular/common/http';
import { ConfigCheckerAuthService } from '../../services/config-checker-auth.service';
import { environment } from '../../../../environments/environment';


export type CheckerMode = 'default' | 'sorted' | 'appPath';


@Component({
  selector: 'app-config-checker',
  templateUrl: 'config-checker.component.html',
  styleUrls: ['config-checker.component.scss']
})
export class ConfigCheckerComponent implements OnInit {

  // checker settings
  checkerSettings: CheckerSettings;

  metricJson: any;


  elementConfig = {};
  pageConfig = {};
  state = {};
  currentStage = '';
  showOverlay = false;
  showFirstPageOverview = false;
  showMissingPageInfo = false;
  params: any = {
    state: {persistentValues: {}},
    media: {},
    languageStore: {},
    blockScreen: false
  };
  languageStore: Record<string, string> = {};
  helperFunctions: any;

  screenshotPaths = [];
  outputPathApp = '';


  // all what needs to be displayed in the config checker
  pageInputs: Array<string> = [];
  pageOutputs: Array<string> = [];
  numberOfReferences = 0;
  numberOfOutputs = 0;
  numberOfInputs = 0;
  isReached = false;
  isDeadEnd = false;
  isRendered = false;
  isNewPage = false;
  isChangedPage = false;

  // Global metrics for overview
  deadEndPages: Array<string> = [];
  unreachablePages: Array<string> = [];
  deadEndCount = 0;
  unreachableCount = 0;
  unreachableErrorCount = 0;
  newPageCount = 0;
  changedPageCount = 0;
  totalIssuesCount = 0;
  totalChangesCount = 0;
  intentionallyUnreachablePage = '00_01 - appTOS';
  intentionallyDeadEndPage = 'previousScreen';
  languageMetrics: any = {};
  configName = '';
  newPages: Array<string> = [];
  changedPages: Array<string> = [];
  changedSettings: Array<string> = [];
  changedTemplates: Array<string> = [];
  changedTemplatesElements: Array<string> = [];
  changedLanguages: any = {};
  itemDisplayAmount = 5;
  expandedOverviewSections = new Set<string>();
  pageRanking: Array<string> = [];
  orderedStages: Array<string> = [];
  appPathNames: Array<string> = [];
  appPathLengths: Array<number> = [];
  currentPathName = '';
  currentStageIndex = 0;
  isViewMode = false;
  pdfGenerated = false;
  metricsEnabled = true;
  pageMatched = true;
  targetPageNumber = 1;

  toggleOverviewSection(section: string): void {
    if (this.expandedOverviewSections.has(section)) {
      this.expandedOverviewSections.delete(section);
      return;
    }

    this.expandedOverviewSections.add(section);
  }

  isOverviewSectionExpanded(section: string): boolean {
    return this.expandedOverviewSections.has(section);
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private checkerDataService: CheckerDataService,
    private http: HttpClient,
    private authService: ConfigCheckerAuthService
  ) {
    const homePage = this.checkerDataService.getHomePage();
    if (homePage) {
      console.log('Using real HomePage instance for data');
      this.params = homePage;
      this.helperFunctions = homePage.helperFunctions;
      this.elementConfig = homePage.elementConfig;
      this.pageConfig = homePage.pageConfig;
      this.state = homePage.state;

      if (this.params) {
        this.params.cd = this.cdr;
      }
    } else {
      console.warn('No HomePage instance found, using mock params');
      this.params.cd = this.cdr;
      this.params.checkElement = () => true;
      this.helperFunctions = new HomePageHelpers(this.params);
    }
  }

  /**
   * Gets the metrics for a specific page from the metricJson using the index number
   * @param idx - The index number of the page in the metricJson
   * NOTE: currently not used, can be used if update via idx is desired
   */
  // noinspection JSUnusedGlobalSymbols
  updatePageMetrics(idx: number){
    if (!this.metricJson) {
      return;
    }
    this.pageInputs = this.metricJson.page_inputs[idx] || [];
    this.pageOutputs = this.metricJson.page_outputs[idx] || [];
    this.numberOfReferences = this.metricJson.number_of_references[idx];
    this.numberOfOutputs = this.metricJson.number_of_outputs[idx];
    this.numberOfInputs = this.metricJson.number_of_inputs[idx];
    this.isReached = this.metricJson.is_reached[idx];
    this.isDeadEnd = this.metricJson.is_dead_end[idx];
    this.isRendered = this.metricJson.is_rendered[idx];
    this.isNewPage = this.metricJson.new_page[idx];
    this.isChangedPage = this.metricJson.page_changed[idx];

  }

  /**
   * Gets the metrics for a specific page from the metricJson using the stage name
   * Uses the predefined stage_lookup object in the metricJson to find the stage metrics
   * @param stage - The index number of the page in the metricJson
   */
  updatePageMetricsByStage(stage: string) {
    if (!this.metricJson) {
      return;
    }
    const currentPageMetrics = this.metricJson.page_lookup[stage];
    this.pageInputs = currentPageMetrics.page_inputs || [];
    this.pageOutputs = currentPageMetrics.page_outputs || [];
    this.numberOfReferences = currentPageMetrics.number_of_references;
    this.numberOfOutputs = currentPageMetrics.number_of_outputs;
    this.numberOfInputs = currentPageMetrics.number_of_inputs;
    this.isReached = currentPageMetrics.is_reached;
    this.isDeadEnd = currentPageMetrics.is_dead_end;
    this.isRendered = currentPageMetrics.is_rendered;
    this.isNewPage = currentPageMetrics.new_page;
    this.isChangedPage = currentPageMetrics.page_changed;
  }

  /**
   * On init, load the settings and calls the renderConfigPage function
   */
  ngOnInit(): void {
    // load settings
    this.checkerSettings = this.checkerDataService.getSettings();
    this.isViewMode = this.checkerSettings?.viewMode || false;

    // fetch metricJson from the server
    const params: any = {
      designTag: this.checkerSettings?.designTag || '',
      defaultDesignTag: this.checkerSettings?.defaultDesignTag || '',
    };
    const baseUrl = this.params.state?.persistentValues?.urlConfigChecker || 'https://api-de.biozoom.net';
    // testing 'http://localhost:3001'

    this.http.get(`${baseUrl}/config-analyzer/evaluate`, {
      params,
      headers: this.authService.getAuthHeaders(),
      withCredentials: true
    }).subscribe((data) => {
      this.metricJson = data;
      // call renderConfigPage on route change
      this.route.paramMap.subscribe(() => {
        void this.renderConfigPage();
      });
    });
  }

  /**
   * Renders the config page and takes screenshots
   */
  async renderConfigPage() {

    if (!this.metricJson) {
      return;
    }

    this.syncLanguageForRenderedPages();

    this.pdfGenerated = false;

    // Calculate the dead end and not reached pages
    this.deadEndPages = [];
    this.unreachablePages = [];

    // gets the page ranking from the standardPaths.json
    this.pageRanking = standardPaths.stage_occurrences;

    // read info from metricJson to for the first page overview
    const pages = this.metricJson.page as Record<string, string>;
    const isReached = this.metricJson.is_reached as Record<string, boolean>;
    const isDeadEnd = this.metricJson.is_dead_end as Record<string, boolean>;
    const isNewPage = this.metricJson.new_page as Record<string, boolean>;
    const isChangedPage = this.metricJson.page_changed as Record<string, boolean>;


    this.configName = this.params.state?.persistentValues?.CONFIG_NAME || 'Unknown Config';

    // initialize page counters
    this.newPageCount = 0;
    this.changedPageCount = 0;

    // initialize all page counts for the page overview
    for (const [idx, name] of Object.entries(pages)) {
      if (isDeadEnd[idx] && name !== this.intentionallyDeadEndPage) {
        this.deadEndPages.push(name);
      }
      if (isReached[idx] === false && name !== this.intentionallyUnreachablePage) {
        this.unreachablePages.push(name);
      }
      if (isNewPage[idx]) {
        this.newPageCount++;
      }
      if (isChangedPage[idx]) {
        this.changedPageCount++;
      }
    }
    this.deadEndCount = this.deadEndPages.length;
    this.unreachableCount = this.unreachablePages.length;
    this.unreachableErrorCount = this.unreachablePages.length;

    this.languageMetrics = this.metricJson.languages || {};

    this.newPages = this.metricJson.new_pages || [];
    this.changedPages = this.metricJson.changed_pages || [];
    this.changedSettings = this.metricJson.changed_settings || [];
    this.changedTemplates = this.metricJson.changed_templates || [];
    this.changedTemplatesElements = this.metricJson.changed_templates_elements || [];
    this.changedLanguages = this.metricJson.changed_languages || {};

    // Calculate the total issues count
    this.totalIssuesCount = this.unreachableCount + this.deadEndCount;
    for (const lang of Object.values(this.languageMetrics) as any[]) {
      this.totalIssuesCount += (lang.missing_keys_count || 0);
      this.totalIssuesCount += (lang.missing_values_count || 0);
    }

    // Calculate the total changes count
    this.totalChangesCount = this.newPages.length +
                             this.changedPages.length +
                             this.changedSettings.length +
                             this.changedTemplates.length +
                             this.changedTemplatesElements.length;

    for (const langKeys of Object.values(this.changedLanguages) as string[][]) {
      this.totalChangesCount += (langKeys.length || 0);
    }

    if (!this.checkerSettings.viewMode && this.checkerSettings.enableMetricOverviewPage) {
      // create a screenshot of the overview page
      this.showFirstPageOverview = true;
      // Wait for the page to be rendered
      this.updateChanges();
      await this.waitForRender();

      const overviewScreenshot = 'screenshot_overview.png';
      await window.electronAPI.screenshot({ outputPath: overviewScreenshot });
      this.screenshotPaths.push(overviewScreenshot);
    }

    if (!this.checkerSettings.viewMode) {
      this.showFirstPageOverview = false;
    }

    // prepare order of rendered pages
    const pageEntries = Object.entries(this.metricJson.page as Record<string, string>);
    const sortedPageEntries = Object.entries(this.metricJson.page as Record<string, string>);
    sortedPageEntries.sort((a, b) => {
      const indexA = this.pageRanking.indexOf(a[1]);
      const indexB = this.pageRanking.indexOf(b[1]);

      // If both are in pageRanking, sort by their position
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
      // If only A is in pageRanking, it comes first
      if (indexA !== -1) {
        return -1;
      }
      // If only B is in pageRanking, it comes first
      if (indexB !== -1) {
        return 1;
      }
      // If neither is in pageRanking, maintain the original order
      return 0;
    });

    // create app paths from path ranking
    const appPaths = standardPaths.path_ranking as [string[], number][];

    // checker Mode selection
    switch (this.checkerSettings.checkerMode) {
      case 'sorted':
        this.orderedStages = sortedPageEntries.map(e => e[1]);
        break;
      case 'appPath':

        this.appPathNames = ['aox', 'hrv', 'vitality', 'refreshConfig', 'menuOverlay', 'WIFI-Pairing'];

        const pathMap: Record<string, number> = {
          aox: 1,
          hrv: 23,
          vitality: 6,
          refreshConfig: 7,
          menuOverlay: 9,
          'WIFI-Pairing': 18,
        };

        const selectedPath = this.checkerSettings.selectedAppPath || 'all';

        if (selectedPath === 'all') {
          this.orderedStages = [
            ...appPaths[1][0],  // aox
            ...appPaths[23][0], // hrv
            ...appPaths[6][0], // vitality
            ...appPaths[7][0], // refreshConfig
            ...appPaths[9][0], // menuOverlay
            ...appPaths[18][0], // WIFI-Pairing
          ];

          this.appPathLengths = [
            appPaths[1][0].length,
            appPaths[23][0].length,
            appPaths[6][0].length,
            appPaths[7][0].length,
            appPaths[9][0].length,
            appPaths[18][0].length,
          ];
        } else {
          const pathIdx = pathMap[selectedPath];
          this.orderedStages = [...appPaths[pathIdx][0]];
          this.appPathLengths = [appPaths[pathIdx][0].length];
          this.appPathNames = [selectedPath];
        }

        break;
      default:
        this.orderedStages = pageEntries.map(e => e[1]);
    }

    if (this.checkerSettings.viewMode) {
      // Filter orderedStages based on settings
      this.orderedStages = this.orderedStages.filter(stage => {
        if (stage === 'fetching config') {
          return false;
        }

        const metrics = this.metricJson.page_lookup[stage];
        if (!metrics) {
          return false;
        }

        if (this.checkerSettings.onlyChanged && !metrics.page_changed) {
          return false;
        }
        if (this.checkerSettings.onlyRendered && !metrics.is_rendered) {
          return false;
        }
        if (this.checkerSettings.onlyNewPages && !metrics.new_page) {
          return false;
        }
        if (this.checkerSettings.onlyReachedPages && !metrics.is_reached) {
          return false;
        }
        return !(this.checkerSettings.onlyDeadEnds && !metrics.is_dead_end);
      });

      // Add overview at the beginning
      this.orderedStages.unshift('overview');

      this.currentStageIndex = 0;
      await this.renderCurrentStage();
      return;
    }

    // init counters
    let currentPathIdx = 0;
    let pagesInCurrentPathCount = 0;

    // main loop
    for (const [idx, stage] of this.orderedStages.entries()) {
      // appPath
      if (this.checkerSettings.checkerMode === 'appPath') {
        if (pagesInCurrentPathCount >= this.appPathLengths[currentPathIdx]) {
          pagesInCurrentPathCount = 0;
          currentPathIdx++;
        }
        this.currentPathName = this.appPathNames[currentPathIdx] || '';
        pagesInCurrentPathCount++;
      }

      // ignore fetching config stage
      if (stage === 'fetching config'){
        continue;
      }

      // update page metrics and handle missing/renamed pages
      try{
        this.updatePageMetricsByStage(stage);
      }
      catch (error){
        console.error('Stage missing or renamed:', stage, error);
        this.currentStage = stage;
        this.showMissingPageInfo = true;
        this.showOverlay = false;
        this.showFirstPageOverview = false;

        await this.waitForRender();

        const screenshotName = `screenshot_${idx}_missing.png`;
        await window.electronAPI.screenshot({ outputPath: screenshotName });
        this.screenshotPaths.push(screenshotName);

        this.showMissingPageInfo = false;
        continue;
      }


      // filter displayed pages
      if (this.checkerSettings.onlyChanged && !this.isChangedPage){
        continue;
      }
      if (this.checkerSettings.onlyRendered && !this.isRendered){
        continue;
      }
      if (this.checkerSettings.onlyNewPages && !this.isNewPage){
        continue;
      }
      if (this.checkerSettings.onlyReachedPages && !this.isReached){
        continue;
      }
      if (this.checkerSettings.onlyDeadEnds && !this.isDeadEnd){
        continue;
      }

      // this.updatePageMetrics(idx);
      this.currentStage = stage;

      // set measurementType to render pages according to appPath
      if (this.isRendered) {

        await this.helperFunctions.executeStage(stage, 'Running ConfigChecker');
        if (this.checkerSettings.checkerMode === 'appPath' && this.appPathNames[currentPathIdx] === 'aox') {
          await this.helperFunctions.setState('measurementType', 'aox', 'temp');
        }
        else if (this.checkerSettings.checkerMode === 'appPath' && this.appPathNames[currentPathIdx] === 'hrv') {
          await this.helperFunctions.setState('measurementType', 'hrv', 'temp');
        }
        else if (this.checkerSettings.checkerMode === 'appPath' && this.appPathNames[currentPathIdx] === 'vitality') {
          await this.helperFunctions.setState('measurementType', 'vitality', 'temp');
        }
        else {
          await this.helperFunctions.setState('measurementType', 'aox', 'temp');
        }

        // update HomePage
        this.updateInputForHomePage();

        // wait for the page to be rendered
        this.updateChanges();
        await this.waitForRender();

        // screenshot with electron app
        await this.sendTriggerToElectronApp(idx);

      }

      if (!this.checkerSettings.enableMetricInfoPage) {
        this.showOverlay = false;
        continue;
      }

      // enable overlay
      this.showOverlay = true;

      // wait for the page to be rendered
      this.updateChanges();
      await this.waitForRender();

      // screenshot with electron app for a meta-data case
      const outputPathMeta = `screenshot_${idx}_meta.png`;
      await window.electronAPI.screenshot({ outputPath: outputPathMeta });
      this.screenshotPaths.push(outputPathMeta);
    }

    // finally, generate the final PDF via electron app
    if (this.screenshotPaths.length > 0) {
      console.log('Generating final PDF from screenshots:', this.screenshotPaths);
      await window.electronAPI.generatePDF({
        imagePaths: this.screenshotPaths,
        outputPath: 'final_report.pdf'
      });
    }

    this.pdfGenerated = true;
    this.updateChanges();
  }

  async nextPage() {
    if (this.currentStageIndex < this.orderedStages.length - 1) {
      this.currentStageIndex++;
      await this.renderCurrentStage();
    }
  }

  async previousPage() {
    if (this.currentStageIndex > 0) {
      this.currentStageIndex--;
      await this.renderCurrentStage();
    }
  }

  async jumpToPage() {
    const pageNumber = Number(this.targetPageNumber);
    if (!Number.isFinite(pageNumber) || this.orderedStages.length === 0) {
      this.targetPageNumber = this.currentStageIndex + 1;
      return;
    }

    const clampedPageNumber = Math.min(Math.max(Math.trunc(pageNumber), 1), this.orderedStages.length);
    this.targetPageNumber = clampedPageNumber;
    this.currentStageIndex = clampedPageNumber - 1;
    await this.renderCurrentStage();
  }

  goBackToSettings() {
    this.router.navigate(['/config-checker-settings']).then();
  }

  async exportPDF() {
    const defaultName = `${this.configName}_report.pdf`.replace(/[\/\\?%*:|"<>]/g, '-');
    const result = await window.electronAPI.exportPDF({
      sourcePath: 'final_report.pdf',
      defaultName
    });

    if (result.success) {
      console.log('PDF exported to:', result.filePath);
    } else if (result.cancelled) {
      console.log('Export cancelled');
    } else {
      console.error('Export failed:', result.error);
    }
  }

  toggleMetrics() {
    this.metricsEnabled = !this.metricsEnabled;
    const stage = this.orderedStages[this.currentStageIndex];
    if (stage && stage !== 'overview' && stage !== 'fetching config') {
      this.showOverlay = this.metricsEnabled;
    }
    this.updateChanges();
  }

  async renderCurrentStage() {
    this.syncLanguageForRenderedPages();

    this.pageMatched = false;
    this.isRendered = false;
    const stage = this.orderedStages[this.currentStageIndex];
    if (!stage) { return; }

    this.targetPageNumber = this.currentStageIndex + 1;

    if (stage === 'overview') {
      this.showFirstPageOverview = true;
      this.showOverlay = false;
      this.showMissingPageInfo = false;
      this.pageMatched = true;
      this.updateChanges();
      return;
    }

    this.showFirstPageOverview = false;

    if (stage === 'fetching config') {
      this.currentStage = stage;
      this.showMissingPageInfo = true;
      this.showOverlay = false;
      this.pageMatched = true;
      this.updateChanges();
      return;
    }

    try {
      this.updatePageMetricsByStage(stage);
      this.showMissingPageInfo = false;
    } catch (error) {
      console.error('Stage missing or renamed:', stage, error);
      this.currentStage = stage;
      this.showMissingPageInfo = true;
      this.showOverlay = false;
      this.pageMatched = true;
      this.updateChanges();
      return;
    }

    this.currentStage = stage;

    if (this.isRendered) {
      await this.helperFunctions.executeStage(stage, 'Running ConfigChecker');
      // Logic for measurementType based on appPath if needed
      if (this.checkerSettings.checkerMode === 'appPath') {
          // find which path we are in
          let count = 0;
          let pathIdx = 0;
          for (let i = 0; i < this.appPathLengths.length; i++) {
              count += this.appPathLengths[i];
              if (this.currentStageIndex < count) {
                  pathIdx = i;
                  break;
              }
          }
          const pathName = this.appPathNames[pathIdx];
          this.currentPathName = pathName;
          if (pathName === 'aox' || pathName === 'hrv' || pathName === 'vitality') {
              await this.helperFunctions.setState('measurementType', pathName, 'temp');
          } else {
              await this.helperFunctions.setState('measurementType', 'aox', 'temp');
          }
      } else {
          await this.helperFunctions.setState('measurementType', 'aox', 'temp');
      }

      this.updateInputForHomePage();
    }

    this.showOverlay = this.isViewMode ? this.metricsEnabled : true;
    this.updateChanges();
    // await this.waitForRender();
    this.pageMatched = true;
    this.updateChanges();
  }

  /**
   * Update input for the HomePage component
   */
  updateInputForHomePage(){
    // need to update via input on HomePage
    this.elementConfig = {...this.params.elementConfig};
    this.pageConfig = {...this.params.pageConfig};
    this.state = {...this.params.state};
    this.languageStore = {...(this.params.languageStore ?? {})};
    this.showOverlay = false;
  }

  private syncLanguageForRenderedPages() {
    const persistentValues = this.params.state?.persistentValues ?? {};
    this.params.state = this.params.state ?? {};
    this.params.state.persistentValues = persistentValues;

    const selectedLanguage = persistentValues.SELECTED_LANGUAGE
      || localStorage.getItem('SELECTED_LANGUAGE')
      || persistentValues.LANGUAGE_TAG
      || this.params.apiBasicService?.getConfiguration?.().defaultLang
      || 'EN-US';
    const languageKey = this.resolveLanguageKey(selectedLanguage);

    persistentValues.SELECTED_LANGUAGE = languageKey;
    persistentValues.LANGUAGE_TAG = languageKey.substring(0, 2).toLowerCase();
    this.params.languageStore = environment.appDesignLanguages?.[languageKey] ?? {};
    this.languageStore = {...this.params.languageStore};
  }

  private resolveLanguageKey(language: string): string {
    const languages = environment.appDesignLanguages ?? {};
    const languageKeys = Object.keys(languages);
    const fallback = languageKeys.find(key => key === 'EN-US') ?? languageKeys[0] ?? 'EN-US';

    if (!language) {
      return fallback;
    }

    const normalized = language.toLowerCase();
    const exactMatch = languageKeys.find(key => key.toLowerCase() === normalized);
    if (exactMatch) {
      return exactMatch;
    }

    const languagePrefix = normalized.substring(0, 2);
    return languageKeys.find(key => key.toLowerCase().substring(0, 2) === languagePrefix) ?? fallback;
  }

  /**
   * wait for changes are detected for the current page
   */
  updateChanges(){
    this.params.cd.detectChanges();
  }

  /**
   * Send trigger to electron app to take screenshot
   * @param idx - The index number of the page in the metricJson
   */
  async sendTriggerToElectronApp(idx: number) {
    this.outputPathApp = `screenshot_${idx}_app.png`;
    await window.electronAPI.screenshot({ outputPath: this.outputPathApp });
    this.screenshotPaths.push(this.outputPathApp);
  }

  /**
   * Wait for the page to be rendered
   */
  async waitForRender() {
    await new Promise(requestAnimationFrame);
    await new Promise(resolve => setTimeout(resolve, 1000));
  }

}
